import React, {useEffect, useState} from "react";
import {Button, Form, Input, Modal, Select, notification} from "antd";
import SharedService from "../../../../services/shared/service";
import {pluralize} from "../../../../utils/string.utils";

const {TextArea} = Input;

const RejectBulkInvoiceModal = ({visible, onCancel, documentIds = [], user}) => {
    const [form] = Form.useForm();
    const [reasonOptions, setReasonOptions] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!visible) return;
        const fetchReasons = async () => {
            try {
                const response = await SharedService.get({
                    token: user?.token,
                    url: "reference/document/rejectreasons",
                });
                const sorted = (response ?? []).sort((a, b) => a.value.localeCompare(b.value));
                setReasonOptions(sorted);
            } catch {
                notification.error({message: "Failed to load reject reasons"});
            }
        };
        fetchReasons();
    }, [visible, user?.token]);

    const handleCancel = () => {
        form.resetFields();
        onCancel?.();
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            await SharedService.patch({
                token: user.token,
                url: "documents/bulk",
                data: {
                    documentIds,
                    rejectReasonId: values.reason,
                    comment: values.notes,
                    status: "rejected",
                    updatedBy: user.email,
                },
            });

            form.resetFields();
            onCancel?.();
            window.location.reload();
        } catch (error) {
            console.error("Bulk rejection failed:", error);
            notification.error({
                message: "Bulk rejection failed",
                description: error?.message,
            });
        } finally {
            setLoading(false);
        }
    };

    const isDisabled = !documentIds.length || loading;

    return (
        <Modal
            title={`Reject ${pluralize(documentIds, "invoice")}`}
            open={visible}
            onCancel={handleCancel}
            closable={true}
            width={560}
            footer={[
                <Button key="cancel" onClick={handleCancel} disabled={loading}>
                    Cancel
                </Button>,
                <Button
                    key="submit"
                    type="primary"
                    danger
                    loading={loading}
                    onClick={handleSubmit}
                    disabled={isDisabled}
                >
                    Reject {documentIds.length || ""}
                </Button>,
            ]}
        >
            <Form form={form} layout="vertical" requiredMark={false}>
                <Form.Item
                    name="reason"
                    rules={[{required: true, message: "Please select a reason"}]}
                    label={
                        <span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">
                            Reason <span style={{color: "#A4231D"}}>*</span>
                        </span>
                    }
                >
                    <Select
                        placeholder="Select a reason"
                        options={reasonOptions.map((opt) => ({
                            label: opt.value,
                            value: opt.id,
                        }))}
                    />
                </Form.Item>

                <Form.Item
                    name="notes"
                    rules={[{required: true, message: "Please enter notes"}]}
                    label={
                        <span className="text-[12px] font-semibold text-[#7A849A] uppercase tracking-wide">
                            Notes <span style={{color: "#A4231D"}}>*</span>
                        </span>
                    }
                >
                    <TextArea
                        autoSize={{minRows: 4, maxRows: 8}}
                        placeholder="Add context for the rejection"
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default RejectBulkInvoiceModal;
